<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  beneficiary: { type: Object, required: true },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['release', 'transfer']);
const { t } = useI18n();
const cents = value => Number(value?.amount_cents || 0);
const totalCents = computed(() => cents(props.beneficiary.valor_total));
const transferredCents = computed(() =>
  Math.min(cents(props.beneficiary.valor_transferido), totalCents.value)
);
const progress = computed(() =>
  totalCents.value > 0
    ? Math.min(
        100,
        Math.round((transferredCents.value / totalCents.value) * 100)
      )
    : 0
);
const beneficiaryName = computed(
  () =>
    props.beneficiary.nome ||
    props.beneficiary.empresa?.name ||
    props.beneficiary.usuario?.name ||
    t('REAL_ESTATE_FINANCE.TABLE.NOT_INFORMED')
);
const statusLabel = computed(() => {
  switch (props.beneficiary.status?.slug) {
    case 'em_transferencia':
      return t('REAL_ESTATE_FINANCE.ALLOCATION_STATUS.TRANSFERRING');
    case 'liberado':
      return t('REAL_ESTATE_FINANCE.ALLOCATION_STATUS.RELEASED');
    case 'pago':
      return t('REAL_ESTATE_FINANCE.ALLOCATION_STATUS.PAID');
    case 'solicitado':
      return t('REAL_ESTATE_FINANCE.ALLOCATION_STATUS.REQUESTED');
    case 'transferido':
      return t('REAL_ESTATE_FINANCE.ALLOCATION_STATUS.TRANSFERRED');
    default:
      return t('REAL_ESTATE_FINANCE.ALLOCATION_STATUS.TO_PAY');
  }
});
</script>

<template>
  <article
    data-testid="finance-commission-allocation-row"
    class="grid gap-4 border-b border-lais-border-soft py-4 last:border-b-0 sm:grid-cols-[minmax(11rem,1fr)_minmax(14rem,1.3fr)_auto_auto] sm:items-center"
  >
    <div>
      <p class="font-semibold text-lais-text">{{ beneficiaryName }}</p>
      <p class="mt-1 text-xs text-lais-text-muted">
        {{
          beneficiary.tipo === 'empresa'
            ? t('REAL_ESTATE_FINANCE.ALLOCATION.COMPANY')
            : t('REAL_ESTATE_FINANCE.ALLOCATION.BROKER')
        }}
      </p>
    </div>
    <div>
      <p class="text-sm text-lais-text">
        {{
          beneficiary.valor_transferido?.amount ||
          t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY')
        }}
        {{ t('REAL_ESTATE_FINANCE.ALLOCATION.OF') }}
        {{
          beneficiary.valor_total?.amount ||
          t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY')
        }}
      </p>
      <progress
        class="mt-2 h-2 w-full accent-lais-primary"
        :class="progress === 100 ? 'accent-lais-success' : ''"
        :value="progress"
        max="100"
      />
    </div>
    <span
      class="w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700"
    >
      {{ statusLabel }}
    </span>
    <div class="flex flex-wrap gap-2 sm:justify-end">
      <LaisPrimaryButton
        v-if="beneficiary.pode_liberar"
        size="sm"
        variant="ghost"
        :disabled="loading"
        @click="emit('release', beneficiary)"
      >
        {{ t('REAL_ESTATE_FINANCE.DETAIL.RELEASE') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="beneficiary.pode_transferir"
        size="sm"
        :disabled="loading"
        @click="emit('transfer', beneficiary)"
      >
        {{ t('REAL_ESTATE_FINANCE.DETAIL.TRANSFER') }}
      </LaisPrimaryButton>
    </div>
  </article>
</template>
